import * as R from 'ramda';
import { createSelector } from 'reselect';
import { Entity, Feature } from '@wayfinder/vms-base';
import { LandscapeState, CanonicalLoader, scheduleEntityEdits } from '@wayfinder/vms-state';
import { ActionConstants as DataStatusReducerActionConstants } from './data-status-reducer';
import def from './define-action';
import { POI_STATUS_TYPE, BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE } from '../constants';
import * as Telemetry from '../telemetry.js';
import * as Routing from './routing';

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    CREATE_POI_FAILURE: 'CREATE_POI_FAILURE',
    EDIT_POI: 'EDIT_POI',
    REVERT_ALL_EDITS: 'REVERT_ALL_EDITS',
    REVERT_POI: 'REVERT_POI',
    SAVE_POI_FAILURE: 'SAVE_POI_FAILURE',
    START_NEW_POI: 'START_NEW_POI',
    CLOSE_FEATURE: 'CLOSE_FEATURE',
    CLEAR_FEATURE: 'CLEAR_FEATURE',
    UPDATE_POI_COORDS: 'UPDATE_POI_COORDS',
    LAST_SAVED_POI_COORDS: 'LAST_SAVED_POI_COORDS',
    SHOW_SUCCESSFUL_POI_MOVE: 'SHOW_SUCCESSFUL_POI_MOVE',
    ...DataStatusReducerActionConstants,
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    createPoiFailure: def(ActionConstants.CREATE_POI_FAILURE, 'venueId', 'errorMessage'),
    editPoi: def(ActionConstants.EDIT_POI, 'uuid', 'changes'),
    revertAllEdits: def(ActionConstants.REVERT_ALL_EDITS),
    revertPoi: def(ActionConstants.REVERT_POI, 'uuid'),
    savePoiFailure: def(ActionConstants.SAVE_POI_FAILURE, 'uuid', 'errorMessage'),
    startNewPoi: def(ActionConstants.START_NEW_POI, 'venueId', 'changes'),
    closeFeature: def(ActionConstants.CLOSE_FEATURE, 'feature'),
    clearFeature: def(ActionConstants.CLEAR_FEATURE),
    updatePoiCoords: def(ActionConstants.UPDATE_POI_COORDS, 'updatePoiCoords'),
    lastSavedPoiCoords: def(ActionConstants.LAST_SAVED_POI_COORDS, 'lastSavedPoiCoords'),
    showSuccessfulPoiMove: def(ActionConstants.SHOW_SUCCESSFUL_POI_MOVE, 'showSuccessfulPoiMove'),
};

const REJECTED_ERROR = 'Some entities were rejected';

// =====================================================================================================================
// Helper Functions
// =====================================================================================================================

const mangledImages = async (venueId, entity, state) => {
    // Generate IDs for the newly generated images and upload the picture
    const getImageIds = field => async image =>
        image.blobId ? Promise.resolve(image.blobId) : CanonicalLoader.uploadPixels(image.file, image.type, field);

    const imagesIds = entity.images ? await Promise.all(R.map(getImageIds('img'), entity.images)) : [];
    const logoId = entity.logo ? await getImageIds('logo')(entity.logo) : null;
    const isV5 = LandscapeState.selectors.isVenueV5(venueId, state);
    // Check if contains image/logo property, and modify change object into something accepted by the RMS
    return R.has('images', entity) || R.has('logo', entity)
        ? CanonicalLoader.imageMangler(entity, imagesIds, logoId, isV5)
        : Promise.resolve(entity);
};

// :: String -> Entity
// Impure: pulls data from Loki
const existingEntity = id => {
    const openId = id.replace(/#/, '');
    const closedId = `#${openId}`;
    return LandscapeState.selectors.findEntity({ id: openId }) || LandscapeState.selectors.findEntity({ id: closedId });
};

// :: { entity: { id: String, ... } -> Entity
// Impure: pulls data from Loki
const mockUpdateExistingEntity = changes => R.mergeRight(existingEntity(changes.entity.id), changes.entity);

const applyBulkEdits = (idList, bulkEditConfiguration) => {
    const rejectNil = R.reject(R.isNil);
    return rejectNil(idList.map(id => fetchOneAndApplyEdits(id, bulkEditConfiguration)));
};

const applySingleEdits = async (venueId, uuids, changes, state) => {
    const id = changes.id || uuids[0].replace(`${venueId}_`, '');
    changes.id = id;
    const mangledChanges = await mangledImages(venueId, changes, state);
    return [{ entity: mangledChanges }];
};

const applyFieldEdit = (originalValue, newValue, changeType) => {
    switch (changeType) {
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.REPLACE:
            return newValue;
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.CATEGORY_CHANGE:
            return newValue;
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.SECURITY_CHANGE:
            return newValue;
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.ICON_CHANGE:
            return newValue;
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.DELETE:
            return R.is(Array, originalValue) ? [] : null;
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.ARRAY_ADD:
            return R.uniq([...originalValue, ...newValue]);
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.ARRAY_REMOVE:
            return R.without([...newValue], originalValue);
        case BULK_CHANGE_CONFIGURATOR_CHANGE_TYPE.STATUS_CHANGE:
            return setStatus(originalValue, newValue);

        default:
            console.error(`unknown bulk edit type applied: ${changeType}. Skipping`);
            return originalValue;
    }
};

const fetchOneAndApplyEdits = (uuid, bulkEditConfiguration) => {
    const entity = LandscapeState.selectors.findEntity({ uuid });

    if (!entity) {
        console.error(`Could not find poi to apply bulk edit, skipping: ${uuid}`);
        return false;
    }

    const result = { id: entity.id };
    R.forEachObjIndexed((bulkEdit, field) => {
        const originalValue = entity[field];
        result[field] = applyFieldEdit(originalValue, bulkEdit.value, bulkEdit.changeType);
    }, bulkEditConfiguration);

    return { entity: result };
};

// TODO: these are cloned from InfoEditor-- centralize in Util before editing
const isClosed = poiId => R.startsWith('#', R.trim(poiId));
const convertToClosed = poiId => (isClosed(poiId) ? poiId : `#${R.trim(poiId)}`);
const convertToOpen = poiId => R.replace(/#/g, '', R.trim(poiId));

const setStatus = (originalId, openOrClosed) => {
    if (openOrClosed === 'open') {
        return convertToOpen(originalId);
    }
    if (openOrClosed === 'closed') {
        return convertToClosed(originalId);
    }
    return originalId;
};

const checkRejectedEntities = pois => {
    if (R.path(['rejected', 'entities', 'length'], pois) > 0) throw new Error(REJECTED_ERROR);
};
// =====================================================================================================================
// Thunks
// =====================================================================================================================

/*
 * Transform the changes object into the object the RMS needs: { entity: Entity }.
 * scheduleEntityEdits will further enrich this object with levelId, showBadge, showName and hasMapChanged.
 * This will then update the entities.
 * @sig updateEntities :: (VenueId, [String], [Object], Boolean, String, [Object]) -> [Entity]
 */
const updateEntities =
    (venueId, uuids, changes, isBulkUpdate, updatedPoiCoords, entities) => async (dispatch, getState) => {
        const isEntityMigrated = LandscapeState.selectors.getIsEntityMigrated(venueId, getState());
        let updates;
        if (isEntityMigrated) {
            updates = isBulkUpdate ? entities : await applySingleEdits(venueId, uuids, entities, getState());
        } else {
            updates = isBulkUpdate
                ? await applyBulkEdits(uuids, changes)
                : await applySingleEdits(venueId, uuids, changes, getState());
        }
        Telemetry.recordEntitiesUpdate(venueId, updates, isBulkUpdate);

        let updatedEntities = await scheduleEntityEdits(
            venueId,
            updates,
            getState(),
            updatedPoiCoords,
            isEntityMigrated,
        );
        checkRejectedEntities(updatedEntities);
        updatedEntities = await Promise.all(
            isEntityMigrated ? R.path(['accepted', 'entities'], updatedEntities) : updatedEntities,
        );
        updatedEntities = updatedEntities[0] === 'MOCKED' ? R.map(mockUpdateExistingEntity, updates) : updatedEntities;

        dispatch(LandscapeState.actionCreators.upsertEntitiesAction(venueId, updatedEntities));

        Telemetry.recordSavePoi(venueId, updatedEntities);

        return updatedEntities;
    };

const createEntityEditsFromMangledData = (mangledData, openLocation = false, oldFeature) => {
    const schemaFields = ['hasMapChanged', 'levelId', 'showBadge', 'showName', 'latLng'];
    const entityEdits = R.omit(schemaFields, mangledData);
    const entity = R.mergeRight(R.pick(schemaFields, mangledData), { entity: entityEdits, hasMapChanged: true });

    if (openLocation) {
        entity.reoccupyPolygon = true;
        entity.oldIdIndexWithinVenue = R.pathOr('', ['properties', 'id'], oldFeature);
    }

    return [entity]; // putFeaturesAndEntities takes an array, even if only one entity is being updated
};

const putFeaturesAndEntities = async (venueId, entities) => {
    const response = await CanonicalLoader.putFeaturesAndEntities(venueId, entities);
    checkRejectedEntities(response);
    return R.path(['accepted', 'entities', 0], response);
};

/*
 * Transform the poiData object into the object the RMS needs to create an entity.
 * Creates an entity.
 * @sig createEntity :: (VenueId, Object, Boolean, Feature) -> Entity
 */
const createEntity = (venueId, poiData, openLocation, oldFeature) => async (dispatch, getState) => {
    if (!poiData.id) poiData.id = String(LandscapeState.selectors.getNextFeatureId(venueId)); // for v4 maps
    poiData.uuid = `${venueId}_${poiData.id}`;

    // Check if contains image/logo property, and modify change object into something accepted by the RMS
    const mangledData = await mangledImages(venueId, poiData, getState());

    const isEntityMigrated = LandscapeState.selectors.getIsEntityMigrated(venueId, getState());

    let newEntity;
    if (openLocation && isEntityMigrated) {
        const entities = createEntityEditsFromMangledData(mangledData, openLocation, oldFeature);
        newEntity = await putFeaturesAndEntities(venueId, entities);
    } else if (openLocation && !isEntityMigrated) {
        const featuresForEntity = LandscapeState.selectors.featuresForEntity(mangledData.id);
        const openLocationBody = Feature.openLocationBody(mangledData, featuresForEntity, oldFeature);
        newEntity = await CanonicalLoader.openLocation(venueId, openLocationBody);
    } else if (isEntityMigrated) {
        // RMS 3.0 create
        const entities = createEntityEditsFromMangledData(mangledData);
        newEntity = await putFeaturesAndEntities(venueId, entities);
    } else {
        // RMS 2.0 create
        newEntity = await CanonicalLoader.createPOI(venueId, mangledData);
    }

    const enriched = Entity.enrichCreatedEntity(newEntity);

    dispatch(LandscapeState.actionCreators.upsertEntitiesAction(venueId, enriched));

    Telemetry.recordSavePoi(venueId, [enriched]);

    return enriched;
};

const getBlob = image => async () => CanonicalLoader.getBlob(image);

const Thunks = {
    updateEntities,
    createEntity,
    getBlob,
};

// =====================================================================================================================
// Reducers
// =====================================================================================================================

const boonPoiStatus = (state = {}, action) => {
    switch (action.type) {
        case ActionConstants.BEGIN_SAVE_POI:
            return R.assoc(action.uuid, POI_STATUS_TYPE.SAVING, state);
        case ActionConstants.SAVE_POI_SUCCESS:
            return R.omit([action.uuid], state);
        case ActionConstants.SAVE_POI_FAILURE:
            return R.assoc(action.uuid, POI_STATUS_TYPE.ERROR, state);
        case ActionConstants.BEGIN_CREATE_POI:
            return R.assoc(`${action.venueId}_NEW`, POI_STATUS_TYPE.DIRTY, state);
        case ActionConstants.CREATE_POI_SUCCESS:
            return R.omit([`${action.venueId}_NEW`], state);
        case ActionConstants.CREATE_POI_FAILURE:
            return R.assoc(`${action.venueId}_NEW`, POI_STATUS_TYPE.ERROR, state);
        case ActionConstants.EDIT_POI:
            return R.assoc(action.uuid, POI_STATUS_TYPE.DIRTY, state);
        case ActionConstants.REVERT_POI:
            return R.omit([action.uuid], state);
        case ActionConstants.REVERT_ALL_EDITS:
            return {};
        default:
            return state;
    }
};

const boonPoiErrorMessage = (state = {}, action) => {
    switch (action.type) {
        case ActionConstants.SAVE_POI_FAILURE:
            return R.assoc(action.uuid, action.errorMessage, state);
        case ActionConstants.BEGIN_CREATE_POI:
            return R.omit([`${action.venueId}_NEW`], state);
        case ActionConstants.CREATE_POI_SUCCESS:
            return R.omit([`${action.venueId}_NEW`], state);
        case ActionConstants.CREATE_POI_FAILURE:
            return R.assoc(`${action.venueId}_NEW`, action.errorMessage, state);
        case ActionConstants.EDIT_POI:
            return R.omit([action.uuid], state);
        case ActionConstants.REVERT_POI:
            return R.omit([action.uuid], state);
        case ActionConstants.REVERT_ALL_EDITS:
            return {};
        default:
            return state;
    }
};

const boonPoiEdits = (state = INITIAL_POI_EDITS_STATE, action) => {
    switch (action.type) {
        case ActionConstants.START_NEW_POI:
            return handleStartNewPoi(state, action);
        case ActionConstants.EDIT_POI:
            return editPoi(state, action);
        case ActionConstants.CREATE_POI_SUCCESS:
            return R.omit([`${action.venueId}_NEW`], state);
        case ActionConstants.SAVE_POI_SUCCESS:
            return R.omit([action.uuid], state);
        case ActionConstants.REVERT_POI:
            return R.omit([action.uuid], state);
        case ActionConstants.REVERT_ALL_EDITS:
            return {};
        case ActionConstants.CLOSE_FEATURE:
            return { ...state, feature: action.feature, openLocation: true };
        case ActionConstants.CLEAR_FEATURE:
            return { ...state, feature: {}, openLocation: false };
        case ActionConstants.UPDATE_POI_COORDS:
            return handleUpdatePoiCoords(state, action);
        case ActionConstants.LAST_SAVED_POI_COORDS:
            return handleLastSavedPoiCoords(state, action);
        case ActionConstants.SHOW_SUCCESSFUL_POI_MOVE:
            return handleShowSuccessfulPoiMove(state, action);
        default:
            return state;
    }
};

const handleUpdatePoiCoords = (state, action) => R.assoc('updatePoiCoords', action.updatePoiCoords, state);

const handleLastSavedPoiCoords = (state, action) => R.assoc('lastSavedPoiCoords', action.lastSavedPoiCoords, state);

const handleShowSuccessfulPoiMove = (state, action) =>
    R.assoc('showSuccessfulPoiMove', action.showSuccessfulPoiMove, state);

const INITIAL_POI_EDITS_STATE = {
    updatePoiCoords: null,
    showSuccessfulPoiMove: false,
};

const editPoi = (state, action) => {
    let newState = state;
    R.forEachObjIndexed((value, key) => {
        newState = R.assocPath([action.uuid, key], value, newState);
    }, action.changes);
    return newState;
};

const handleStartNewPoi = (state, action) => ({
    ...state,
    [`${action.venueId}_NEW`]: action.changes || { venueId: action.venueId, security: 'Outside' },
});

const reducers = {
    boonPoiEdits,
    boonPoiErrorMessage,
    boonPoiStatus,
};

// =====================================================================================================================
// Selectors
// =====================================================================================================================

const getPoiStatus = masterState => R.prop('boonPoiStatus', masterState); // state.boonPoiStatus
const getPoiErrors = masterState => R.prop('boonPoiErrorMessage', masterState); // state.boonPoiErrorMessage
const getPoiEdits = masterState => R.prop('boonPoiEdits', masterState); // state.boonPoiEdits
const getOpenLocation = masterState => R.path(['boonPoiEdits', 'openLocation'], masterState);
const getFeature = masterState => R.path(['boonPoiEdits', 'feature'], masterState);

const isSaving = masterState => R.any(status => status === 'SAVING', R.values(getPoiStatus(masterState)));

const getUpdatePoiCoords = masterState => R.path(['boonPoiEdits', 'updatePoiCoords'], masterState);
const getLastSavedPoiCoords = masterState => R.path(['boonPoiEdits', 'lastSavedPoiCoords'], masterState);
const getShowSuccessfulPoiMove = masterState => R.path(['boonPoiEdits', 'showSuccessfulPoiMove'], masterState);
/*
 * Is the current POI marked DIRTY?
 * @sig isCurrentEntityDirty = (state) => {
 */
const isCurrentEntityDirty = masterState => {
    const poiId = Routing.Selectors.getLocationPoiId(masterState);
    return getPoiStatus(masterState)[poiId] === 'DIRTY';
};

const getFailedPoiSaves = createSelector([getPoiStatus, getPoiErrors, getPoiEdits], (status, errors, edits) => {
    const result = [];
    R.forEachObjIndexed((value, poiId) => {
        if (value === POI_STATUS_TYPE.ERROR) {
            const name =
                R.prop(poiId.name, edits) ||
                R.propOr('Unnamed POI', 'name', LandscapeState.selectors.findEntity({ uuid: poiId }));
            result.push({ id: poiId, name });
        }
    }, status);
    return result;
});

const Selectors = {
    getPoiStatus,
    getPoiErrors,
    getPoiEdits,
    getFailedPoiSaves,
    isCurrentEntityDirty,
    isSaving,
    getOpenLocation,
    getFeature,
    getUpdatePoiCoords,
    getLastSavedPoiCoords,
    getShowSuccessfulPoiMove,
};

export default reducers;

export { reducers, Actions, Selectors, Thunks };
